// Series builder and session helpers: pure functions over the columnar payload (no DOM).
import type { ChartMeta, ChartPayload, ChartSymbol } from "../api/types";
import { dur, fmtClock, fmtIso, fmtLong, fmtLongEt } from "./format";
import type { ObservedFrozenWindow, OracleStep, SeriesRow, Span, SymbolSeries } from "./types";

type Triple = [seen: number, price: number, updatedAt: number];
function triples(entry: ChartSymbol): Triple[] {
  const out: Triple[] = [];
  for (const o of entry.oracle) {
    const [seen, price, at] = o;
    if (seen !== undefined && price !== undefined && at !== undefined) out.push([seen, price, at]);
  }
  return out;
}

/** Rows [t, oracle, updatedAt, pool, basis] from the columnar entry. Oracle steps carry the round current at each sample. */
export function buildSeries(entry: ChartSymbol): SeriesRow[] {
  const base = entry.t0 * 1000;
  const rounds = triples(entry);
  const rows: SeriesRow[] = [];
  let k = -1;
  let oracle: number | null = null;
  let updatedAt: number | null = null;
  for (let i = 0; i < entry.t.length; i++) {
    const off = entry.t[i];
    if (off === undefined) continue;
    let next = rounds[k + 1];
    while (next && next[0] <= off) {
      k++;
      oracle = next[1];
      updatedAt = base + next[2] * 1000;
      next = rounds[k + 1];
    }
    const pool = entry.pool[i] ?? null;
    const basis = pool != null && oracle != null && oracle !== 0 ? (pool / oracle - 1) * 100 : null;
    rows.push({ t: base + off * 1000, oracle, updatedAt, pool, basis, src: "monitor" });
  }
  return rows;
}

/** Oracle rounds as {at, seen, price} in the payload's order (chronological by on-chain updatedAt). */
export function oracleSteps(entry: ChartSymbol): OracleStep[] {
  const base = entry.t0 * 1000;
  return triples(entry).map(([seen, price, at]) => ({ at: base + at * 1000, seen: base + seen * 1000, price }));
}

/** The round in force at `ms`, or the first round known (the one in force through the freeze). */
export function oracleAt(steps: OracleStep[], ms: number): OracleStep | null {
  let cur: OracleStep | null = null;
  for (const s of steps) {
    if (s.at <= ms) cur = s;
    else break;
  }
  return cur ?? steps[0] ?? null;
}

/** Hourly prefix (GeckoTerminal closes) before the monitor started, priced against the round in force then. */
export function hourlyRows(entry: ChartSymbol): SeriesRow[] {
  const hourly = entry.hourly;
  if (!hourly?.length) return [];
  const steps = oracleSteps(entry);
  const rows: SeriesRow[] = [];
  for (const pair of hourly) {
    const [t, close] = pair;
    if (t === undefined || close === undefined) continue;
    const ms = t * 1000;
    const cur = oracleAt(steps, ms);
    const oracle = cur?.price ?? null;
    rows.push({ t: ms, oracle, updatedAt: cur?.at ?? null, pool: close, basis: oracle ? (close / oracle - 1) * 100 : null, src: "hourly" });
  }
  return rows;
}

/** Session windows that overlap [t0, t1], with their bounds parsed to ms. */
export function windowsIn<W extends { from: string; to: string }>(list: W[] | undefined, t0: number, t1: number): (W & Span)[] {
  return (list ?? []).map((w) => ({ ...w, a: Date.parse(w.from), b: Date.parse(w.to) })).filter((w) => w.b > t0 && w.a < t1);
}

/** Recorded rows plus the live rows that fall after the last recorded sample. */
export function seriesFor(entry: ChartSymbol, live: SeriesRow[] = []): SymbolSeries {
  const rec = hourlyRows(entry).concat(buildSeries(entry));
  const lastRec = rec.at(-1);
  const ext = live.filter((r) => !lastRec || r.t > lastRec.t);
  return { entry, rec, ext, all: rec.concat(ext) };
}

export function liveOracleFor(s: SymbolSeries): number | null {
  const last = s.ext.at(-1);
  return last?.oracle ?? null;
}

/**
 * Plain-text summary of the last freeze window for the paragraph under the chart. Works on the static file
 * (windows carry "observed" round times) and on the worker API (no "observed"; the rounds before and after the
 * window come from the symbol's own oracle steps, and the list may end with the coming frozen window).
 */
export function freezeSummary(data: ChartPayload, sym: string, now: number = Date.now()): string {
  const ws: ObservedFrozenWindow[] = data.sessions.frozen;
  const started = ws.filter((w) => Date.parse(w.from) <= now);
  const w = started.at(-1) ?? ws[0];
  if (!w) return "No frozen window falls inside the recorded range.";
  const o = w.observed ?? {};
  const a = Date.parse(w.from), b = Date.parse(w.to);
  const entry = data.symbols[sym];
  const steps = entry ? oracleSteps(entry) : [];
  // The static file's meta lacks from/to (it has covered_from/covered_to); read every key as optional.
  const meta: Partial<ChartMeta> = data.meta;
  const from = meta.covered_from ?? meta.from, to = meta.covered_to ?? meta.to;
  const coverStart = from ? Date.parse(from) : NaN;
  const stepBefore = [...steps].reverse().find((st) => st.at < a);
  // A round after the window is only known when the samples start before the window ended.
  const stepAfter = coverStart <= b ? steps.find((st) => st.at >= b) : null;
  const before = o.last_update_before?.[sym] ?? (stepBefore ? new Date(stepBefore.at).toISOString() : null);
  const after = o.first_update_after?.[sym] ?? (stepAfter ? new Date(stepAfter.at).toISOString() : null);
  const n = o.first_update_after ? Object.keys(o.first_update_after).length : 0;
  const inside = o.updates_inside_window ? Object.keys(o.updates_inside_window).length : 0;
  const samples = meta.samples ?? (entry ? entry.t.length : null);
  let s: string;
  if (a > now) {
    s = `Session rule: the feed is next frozen from ${fmtLongEt(a)} (${fmtLong(a)}) to ${fmtLongEt(b)} (${fmtLong(b)}), ${w.reason}. `;
  } else {
    s = `Session rule: the feed is frozen from ${fmtLongEt(a)} (${fmtLong(a)}) to ${fmtLongEt(b)} (${fmtLong(b)}), ${w.reason}. `;
    s += `Recorded: the ${sym} feed's last round before the window was at ${before ? fmtIso(Date.parse(before)) : "a time outside the recording"}`;
    if (b > now) s += `; the window is still open. `;
    else s += after ? ` and its first round after it at ${fmtIso(Date.parse(after))}, ${dur(Date.parse(after) - b)} after the session reopened. ` : `; no round after the window is in the recording. `;
    if (n && o.first_update_after_earliest && o.first_update_after_latest) {
      s += `${n} of ${String(o.symbols_with_data)} recorded feeds printed a new round between ${fmtClock(Date.parse(o.first_update_after_earliest))} and ${fmtClock(Date.parse(o.first_update_after_latest))}`;
      s += inside ? `; ${inside} updated inside the window. ` : `; none updated inside the window. `;
    }
  }
  if (samples != null && from && to) {
    s += `Samples: ${samples} at a ${((meta.cadence_s ?? NaN) / 60).toFixed(1)} minute cadence, ${fmtIso(Date.parse(from))} to ${fmtIso(Date.parse(to))}${meta.source === "worker" ? ", from the worker" : ""}.`;
  }
  return s;
}
